import React, { useState } from 'react';
import { playNote } from '../../utils/audioUtils';
type NotePosition = {
  line: number; // 0-7, maps to staff position
  name: string;
  pitch: string;
  staffPosition: number; // actual position on staff (0 = bottom, higher = up)
};
const NOTES: NotePosition[] = [{
  line: 0,
  name: 'Do',
  pitch: '1',
  staffPosition: 0
}, {
  line: 1,
  name: 'Re',
  pitch: '2',
  staffPosition: 1
}, {
  line: 2,
  name: 'Mi',
  pitch: '3',
  staffPosition: 2
}, {
  line: 3,
  name: 'Fa',
  pitch: '4',
  staffPosition: 3
}, {
  line: 4,
  name: 'Sol',
  pitch: '5',
  staffPosition: 4
}, {
  line: 5,
  name: 'La',
  pitch: '6',
  staffPosition: 5
}, {
  line: 6,
  name: 'Si',
  pitch: '7',
  staffPosition: 6
}, {
  line: 7,
  name: "Do'",
  pitch: "1'",
  staffPosition: 7
}];
const NOTE_COLORS = ['#FF6B6B', '#FFB347', '#FFEB3B', '#98D8AA', '#87CEEB', '#DDA0DD', '#FFB6C1', '#FF6B6B' // Do' - red
];
export function StaffNotation() {
  const [placedNotes, setPlacedNotes] = useState<NotePosition[]>([]);
  const [selectedNote, setSelectedNote] = useState<NotePosition | null>(null);
  const [activeNoteIndex, setActiveNoteIndex] = useState<number | null>(null);
  // When a note is selected from palette, add it to the staff
  const handleNoteSelect = (note: NotePosition) => {
    setSelectedNote(note);
    playNote(note.pitch);
    // Add note to placed notes
    if (placedNotes.length < 8) {
      setPlacedNotes([...placedNotes, note]);
      setActiveNoteIndex(placedNotes.length);
      setTimeout(() => setActiveNoteIndex(null), 300);
    }
  };
  const playAllNotes = async () => {
    for (let i = 0; i < placedNotes.length; i++) {
      setActiveNoteIndex(i);
      playNote(placedNotes[i].pitch);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    setActiveNoteIndex(null);
  };
  const clearNotes = () => {
    setPlacedNotes([]);
    setSelectedNote(null);
  };
  const removeLastNote = () => {
    if (placedNotes.length > 0) {
      setPlacedNotes(placedNotes.slice(0, -1));
    }
  };
  // Calculate note position on staff
  // staffPosition 0 = below staff (ledger line), 7 = above staff
  const getNoteTopPosition = (staffPosition: number) => {
    // Map 0-7 to visual positions
    // 0 (Do) = 95% (below staff with ledger line)
    // 1 (Re) = 85%
    // 2 (Mi) = 75% (on first line)
    // 3 (Fa) = 65%
    // 4 (Sol) = 55% (on third line - middle)
    // 5 (La) = 45%
    // 6 (Si) = 35% (on fifth line)
    // 7 (Do') = 25% (above fifth line)
    const positions = [88, 78, 68, 58, 48, 38, 28, 18];
    return positions[staffPosition];
  };
  return <div className="flex flex-col items-center w-full max-w-4xl mx-auto" data-id="element-195">
      {/* Staff area */}
      <div className="bg-white rounded-3xl p-8 w-full mb-8 shadow-lg" data-id="element-196">
        <div className="relative h-56 mb-8" data-id="element-197">
          {/* Staff lines - 5 lines */}
          {[0, 1, 2, 3, 4].map(i => <div key={i} className="absolute w-full h-0.5 bg-gray-300" style={{
          top: `${28 + i * 12}%`
        }} data-id="element-198" />)}

          {/* Treble clef */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-6xl text-gray-400 select-none" data-id="element-199">
            𝄞
          </div>

          {/* Placed notes - displayed based on their musical position */}
          {placedNotes.map((note, i) => {
          const topPos = getNoteTopPosition(note.staffPosition);
          const needsLedgerLine = note.staffPosition === 0; // Do needs ledger line
          return <div key={i} className="absolute" style={{
            left: `${15 + i * 10}%`
          }} data-id="element-200">
                {/* Ledger line for low Do */}
                {needsLedgerLine && <div className="absolute w-12 h-0.5 bg-gray-400 -translate-x-1/2" style={{
              top: `${topPos}%`,
              left: '50%'
            }} data-id="element-201" />}
                {/* Note */}
                <div className={`absolute w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm transition-all shadow-md -translate-x-1/2 -translate-y-1/2 ${activeNoteIndex === i ? 'scale-125 shadow-xl ring-4 ring-white' : ''}`} style={{
              backgroundColor: NOTE_COLORS[note.line],
              top: `${topPos}%`,
              left: '50%'
            }} data-id="element-202">
                  <span className="text-base" data-id="element-203">{note.name.charAt(0)}</span>
                </div>
              </div>;
        })}

          {/* Empty slots indicator */}
          {placedNotes.length === 0 && <div className="absolute inset-0 flex items-center justify-center" data-id="element-204">
              <p className="text-gray-300 text-lg" data-id="element-205">点击下方音符添加到五线谱</p>
            </div>}
        </div>

        {/* Controls */}
        <div className="flex justify-center gap-4" data-id="element-206">
          <button onClick={playAllNotes} disabled={placedNotes.length === 0} className="px-6 py-3 bg-[#2196F3] text-white rounded-full font-bold hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-207">
            <span data-id="element-208">播放</span>
            <span data-id="element-209">▶</span>
          </button>
          <button onClick={removeLastNote} disabled={placedNotes.length === 0} className="px-6 py-3 bg-orange-100 text-orange-600 rounded-full font-bold hover:bg-orange-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-210">
            撤销
          </button>
          <button onClick={clearNotes} disabled={placedNotes.length === 0} className="px-6 py-3 bg-gray-100 text-gray-600 rounded-full font-bold hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-211">
            清除
          </button>
        </div>
      </div>

      {/* Note palette */}
      <div className="bg-white rounded-2xl p-6 shadow-lg w-full" data-id="element-212">
        <h3 className="text-lg font-bold text-gray-800 mb-4 text-center" data-id="element-213">
          点击音符添加到五线谱 ({placedNotes.length}/8)
        </h3>
        <div className="flex justify-center gap-3 flex-wrap" data-id="element-214">
          {NOTES.map((note, i) => <button key={note.name} onClick={() => handleNoteSelect(note)} disabled={placedNotes.length >= 8} className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center text-white font-bold transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${selectedNote?.name === note.name ? 'ring-4 ring-offset-2 ring-blue-400 scale-110' : ''}`} style={{
          backgroundColor: NOTE_COLORS[i]
        }} data-id="element-215">
              <span className="text-xl" data-id="element-216">{note.name}</span>
              <span className="text-xs opacity-80" data-id="element-217">{note.pitch}</span>
            </button>)}
        </div>
      </div>

      {/* Visual guide - showing note positions */}
      <div className="mt-6 bg-blue-50 rounded-xl p-4 w-full max-w-md" data-id="element-218">
        <h4 className="font-bold text-blue-800 mb-2 text-center" data-id="element-219">
          🎵 五线谱小知识
        </h4>
        <p className="text-blue-600 text-sm text-center" data-id="element-220">
          音符越高，在五线谱上的位置越高。
          <br data-id="element-221" />
          Do(1) 在下加一线，Sol(5) 在第三线（中间）。
        </p>
      </div>

      {/* Instructions */}
      <div className="mt-4 text-center text-gray-500 max-w-md" data-id="element-222">
        <p className="text-sm" data-id="element-223">
          点击彩色音符 → 自动添加到五线谱 → 点击"播放"听旋律！
        </p>
      </div>
    </div>;
}